import { useSelector } from '@legendapp/state/react';
import {
	Box,
	Button,
	createModal,
	Divider,
	Flex,
	Select,
	Stack,
	Textarea,
	TextInput,
	TFNotifier,
	useModalControl,
} from '@tf/ui';
import { C } from '@tf/utils';
import * as jsonpatch from 'fast-json-patch';
import type { ReplaceOperation } from 'fast-json-patch/commonjs/core';
import { Controller, useForm } from 'react-hook-form';

import { useCreateInwardDetailsMutation, useUpdateInwardDetailsMutation } from '@/core/api/payments';
import { appStore } from '@/core/stores';
import type { InwardDetails } from '@/core/types/payments';

interface Props {
	id: string;
	paymentId: string;
	inwardDetails?: InwardDetails;
}

export const EditInstructionModal = createModal(({ paymentId, inwardDetails, id }: Props) => {
	const modalControl = useModalControl();

	const { control, handleSubmit } = useForm<InwardDetails>({
		defaultValues: {
			parsed_data: {
				sender_jurisdiction: inwardDetails?.parsed_data?.sender_jurisdiction ?? '',
				sender_name: inwardDetails?.parsed_data?.sender_name ?? '',
				sender_iban: inwardDetails?.parsed_data?.sender_iban ?? '',
				sender_bank_jurisdiction: inwardDetails?.parsed_data?.sender_bank_jurisdiction ?? '',
				sender_bank_name: inwardDetails?.parsed_data?.sender_bank_name ?? '',
			},
			purpose_of_payment: inwardDetails?.purpose_of_payment ?? '',
		},
	});

	const createInwardDetailsMutation = useCreateInwardDetailsMutation();
	const updateInwardDetailsMutation = useUpdateInwardDetailsMutation();

	const options = useSelector(() => appStore.defs.options.get());
	const countiesOptions = options.common__enum__country;

	const onSubmit = (data: InwardDetails) => {
		const submitOptions = {
			onSuccess: modalControl.onClose,
			onError: () => {
				TFNotifier.error('Error while updating inward details');
			},
		};
		if (inwardDetails) {
			const diff = jsonpatch.compare(inwardDetails, data);
			const diffWithoutRemovedAttachments = diff.filter((d) => d.op !== 'remove') as ReplaceOperation<string>[];

			updateInwardDetailsMutation.mutate({ formId: id, diff: diffWithoutRemovedAttachments }, submitOptions);
		} else {
			createInwardDetailsMutation.mutate({ paymentId, form: data }, submitOptions);
		}
	};

	return (
		<Box mt="32px" sx={{ borderRadius: '8px', background: '#fff', overflow: 'hidden' }}>
			<form onSubmit={handleSubmit(onSubmit)}>
				<Stack gap="12px" p="12px">
					<Controller
						name="parsed_data.sender_jurisdiction"
						control={control}
						rules={{ required: true }}
						render={({ field, fieldState }) => {
							const baseOptions = C.groupCountries(countiesOptions);
							const hasValue = countiesOptions.some((o) => o.value === field.value);
							const options = hasValue ? baseOptions : [{ value: field.value!, label: field.value! }, ...baseOptions];
							return (
								<Select
									searchable
									label="Sender jurisdiction"
									placeholder="Select country"
									error={Boolean(fieldState.error)}
									data={options}
									{...field}
								/>
							);
						}}
					/>

					<Controller
						name="parsed_data.sender_name"
						control={control}
						rules={{ required: true }}
						render={({ field, fieldState }) => (
							<TextInput
								label="Sender name"
								placeholder="Enter sender name"
								error={Boolean(fieldState.error)}
								{...field}
							/>
						)}
					/>

					<Controller
						name="parsed_data.sender_iban"
						control={control}
						rules={{ required: true }}
						render={({ field, fieldState }) => (
							<TextInput label="Sender IBAN" placeholder="Enter IBAN" error={Boolean(fieldState.error)} {...field} />
						)}
					/>

					<Controller
						name="parsed_data.sender_bank_jurisdiction"
						control={control}
						rules={{ required: true }}
						render={({ field, fieldState }) => {
							const baseOptions = C.groupCountries(countiesOptions);
							const hasValue = countiesOptions.some((o) => o.value === field.value);
							const options = hasValue ? baseOptions : [{ value: field.value!, label: field.value! }, ...baseOptions];
							return (
								<Select
									searchable
									label="Sender bank jurisdiction"
									placeholder="Select country"
									error={Boolean(fieldState.error)}
									data={options}
									{...field}
								/>
							);
						}}
					/>

					<Controller
						name="parsed_data.sender_bank_name"
						control={control}
						rules={{ required: true }}
						render={({ field, fieldState }) => (
							<TextInput
								label="Sender bank name"
								placeholder="Enter bank name"
								error={Boolean(fieldState.error)}
								{...field}
							/>
						)}
					/>

					<Controller
						name="purpose_of_payment"
						control={control}
						render={({ field, fieldState }) => (
							<Textarea
								label="Sender to receiver information"
								placeholder="Enter sender to receiver information"
								error={Boolean(fieldState.error)}
								minRows={3}
								{...field}
							/>
						)}
					/>
				</Stack>
				<Divider c="E5E7EB" size={0.5} />
				<Flex m="12px" justify="end" mt="md">
					<Button disabled={createInwardDetailsMutation.isPending} type="submit">
						Submit
					</Button>
				</Flex>
			</form>
		</Box>
	);
});
